import { getTenant } from '../../../theme/tokens'
import { nowMs } from '../../../shared/lib/relativeDates'
import { makeCompanyName } from '../../../shared/config/localeProfile'
import { BRAND } from '../../../shared/config/brand'

// ── Domain types ─────────────────────────────────────────────────────────

export type PayoutStatus =
  | 'pending'
  | 'submitted'
  | 'partially_submitted'
  | 'failed'
  | 'cancelled'

export type TransferStatus = 'pending' | 'submitted' | 'failed' | 'cancelled'

/**
 * Display status — derived from PayoutStatus.
 *
 * We deliberately don't model an "in transit" / "funds arriving" state.
 * Once a payout is submitted to CIMB we can't reliably observe
 * when the destination account is credited (banks take 1–5 business days
 * and don't notify us), so claiming "in transit" would be making a
 * prediction we can't back up. Every submitted payout shows as 'paid' with
 * a label of "Sent" — i.e. we've handed it off, and the merchant should
 * check their bank for arrival.
 */
export type DisplayStatus =
  | 'paid'
  | 'scheduled'
  | 'partially_submitted'
  | 'failed'
  | 'cancelled'

// ── Dynamic payout calculation model ──────────────────────────────────────
// A payout is Gross amount + a SIGNED list of adjustments. The list is
// open-ended: any number of current or future adjustment kinds can appear.
// Net payout is ALWAYS grossAmount + sum(adjustments). We never model a
// fixed set of financial columns — new adjustment types simply show up as
// new line items in the Payout calculation.
// Stable, merchant-facing line-item kinds. This set maps 1:1 to the labels
// shown in the Payout calculation and is bank/market agnostic — subscription
// charges, terminal rental, service or statement charges all surface as the
// single generic "Account charges" kind; the underlying subtype is never
// exposed here.
export type PayoutAdjustmentKind =
  // Payment activity
  | 'refunds'
  | 'chargebacks'
  | 'chargeback_reversals'
  // Fees and taxes
  | 'fees'
  | 'vat_on_fee'
  | 'wht_adjustment'
  // Reserve adjustments
  | 'reserve_withheld'
  | 'reserve_released'
  // Minimum balance adjustments
  | 'minimum_balance_retained'
  | 'minimum_balance_released'
  // Account charges
  | 'account_charges'
  | 'vat_on_account_charges'
  | 'wht_on_account_charges'
  // Other adjustments
  | 'manual_adjustment'
  | 'negative_balance_recovery'
  | 'other_adjustment'

export type PayoutCalcGroup =
  | 'payment_activity'
  | 'fees_taxes'
  | 'reserve_adjustments'
  | 'minimum_balance_adjustments'
  | 'account_charges'
  | 'other_adjustments'

export interface PayoutLineItem {
  kind: PayoutAdjustmentKind
  label: string
  group: PayoutCalcGroup
  /** Signed. Negative reduces the payout, positive increases it. */
  amount: number
}

const ADJUSTMENT_META: Record<
  PayoutAdjustmentKind,
  { label: string; group: PayoutCalcGroup }
> = {
  // Payment activity
  refunds: { label: 'Refunds', group: 'payment_activity' },
  chargebacks: { label: 'Chargebacks', group: 'payment_activity' },
  chargeback_reversals: {
    label: 'Chargeback reversals',
    group: 'payment_activity',
  },
  // Fees and taxes
  fees: { label: 'Transaction fees', group: 'fees_taxes' },
  vat_on_fee: { label: 'VAT on transaction fees', group: 'fees_taxes' },
  wht_adjustment: { label: 'WHT adjustment', group: 'fees_taxes' },
  // Reserve adjustments
  reserve_withheld: {
    label: 'Reserve withheld',
    group: 'reserve_adjustments',
  },
  reserve_released: {
    label: 'Reserve released',
    group: 'reserve_adjustments',
  },
  // Minimum balance adjustments
  minimum_balance_retained: {
    label: 'Minimum balance retained',
    group: 'minimum_balance_adjustments',
  },
  minimum_balance_released: {
    label: 'Minimum balance released',
    group: 'minimum_balance_adjustments',
  },
  // Account charges — generic merchant-facing billing term. The individual
  // account-charge subtype (subscription, terminal rental, service, statement)
  // is intentionally NOT exposed in the payout calculation.
  account_charges: { label: 'Account charges', group: 'account_charges' },
  vat_on_account_charges: {
    label: 'VAT on account charges',
    group: 'account_charges',
  },
  wht_on_account_charges: {
    label: 'WHT adjustment on account charges',
    group: 'account_charges',
  },
  // Other adjustments
  manual_adjustment: { label: 'Manual adjustment', group: 'other_adjustments' },
  negative_balance_recovery: {
    label: 'Negative balance recovery',
    group: 'other_adjustments',
  },
  other_adjustment: { label: 'Other adjustment', group: 'other_adjustments' },
}

const GROUP_LABEL: Record<PayoutCalcGroup, string> = {
  payment_activity: 'Payment activity',
  fees_taxes: 'Fees and taxes',
  reserve_adjustments: 'Reserve adjustments',
  minimum_balance_adjustments: 'Minimum balance adjustments',
  account_charges: 'Account charges',
  other_adjustments: 'Other adjustments',
}

export interface PayoutTransfer {
  transferId: string
  status: TransferStatus
  /** Transfers only surface the net amount they carry. */
  netAmount: number
  bankReference: string
  description: string
}

export interface Payout {
  payoutId: string
  merchantEntity: string
  status: PayoutStatus
  /** Human-readable effective date (e.g. "May 6, 2026 15:00:00 MVT") */
  effectiveDate: string
  /** Parsed effective date for timeline & status derivation */
  effectiveDateRaw: Date
  currency: string
  grossAmount: number
  /** Signed, only-applicable line items. Net = gross + sum(adjustments). */
  adjustments: PayoutLineItem[]
  /** Signed sum of all adjustments. */
  netAdjustments: number
  /** grossAmount + netAdjustments. */
  netAmount: number
  destinationAccount: string
  transfers: PayoutTransfer[]
}

interface RawTransferEntry {
  merchantEntity: string
  payoutId: string
  payoutStatus: 'Submitted' | 'Pending' | 'Failed'
  payoutEffectiveDate: string
  currency: string
  grossAmount: number
  destinationAccount: string
  description: string
  externalPayoutReference: string
}

// ── Source data ──────────────────────────────────────────────────────────
// All payouts settle into the merchant's CIMB account. Suffix
// matches the bankSuffix mock pattern used elsewhere in the balance feature.

const DESTINATION = `${BRAND.shortCode} •••• 4521`

// Bank references in the raw data carry a legacy "BML-" prefix; surface them
// with the active brand's short code so labels follow the tenant.
function displayBankRef(ref: string): string {
  return ref.replace(/^BML/, BRAND.shortCode)
}

function rawTransfer(
  payoutId: string,
  status: 'Submitted' | 'Pending' | 'Failed',
  effectiveDate: string,
  gross: number,
  _deductions: number, // Ignored — adjustments are computed at payout level
  description: string,
  externalRef: string,
): RawTransferEntry {
  return {
    merchantEntity: makeCompanyName(`${BRAND.name} Merchant`),
    payoutId,
    payoutStatus: status,
    payoutEffectiveDate: effectiveDate,
    currency: getTenant().currency.code,
    grossAmount: gross,
    destinationAccount: DESTINATION,
    description,
    externalPayoutReference: externalRef,
  }
}

const rawTransfers: RawTransferEntry[] = [
  rawTransfer(
    'up_pending_may6_2026_001',
    'Pending',
    'May 6, 2026 15:00:00 MVT',
    46630.72,
    1399.92,
    'Upcoming payout for 6 May 2026',
    'BML-20260506-TXN-00185',
  ),
  rawTransfer(
    '69a608748649da0d81711cc',
    'Submitted',
    'May 7, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '69a608748649da0d81712dd',
    'Submitted',
    'May 7, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '2 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '69a608748649da0d81713ee',
    'Submitted',
    'May 7, 2026 15:00:00 MVT',
    1847300.0,
    53572.7,
    '3 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '7a1b2c3d4e5f60718a9b0c1',
    'Submitted',
    'May 6, 2026 15:00:00 MVT',
    1245080.0,
    36107.32,
    '1 of 1 for 6 May 2026',
    'BML-20260506-TXN-00183',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d1e2',
    'Submitted',
    'May 5, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d2f3',
    'Submitted',
    'May 5, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '2 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d3g4',
    'Submitted',
    'May 5, 2026 15:00:00 MVT',
    210500.0,
    6104.5,
    '3 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '9c3d4e5f6g7h81930k1l2m3',
    'Submitted',
    'May 4, 2026 15:00:00 MVT',
    875000.0,
    25375.0,
    '1 of 1 for 4 May 2026',
    'BML-20260504-TXN-00175',
  ),
  rawTransfer(
    '0d4e5f6g7h8i92041l2m3n4',
    'Submitted',
    'May 3, 2026 15:00:00 MVT',
    567120.0,
    16446.48,
    '1 of 1 for 3 May 2026',
    'BML-20260503-TXN-00172',
  ),
  rawTransfer(
    'ae5f6g7h8i9j03152m3n4o5',
    'Submitted',
    'May 2, 2026 15:00:00 MVT',
    1530000.0,
    44370.0,
    '1 of 2 for 2 May 2026',
    'BML-20260502-TXN-00168',
  ),
  rawTransfer(
    'bf6g7h8i9j0k14263n4o5p6',
    'Submitted',
    'May 2, 2026 15:00:00 MVT',
    980500.0,
    28434.5,
    '2 of 2 for 2 May 2026',
    'BML-20260502-TXN-00168',
  ),
  rawTransfer(
    'cg7h8i9j0k1l25374o5p6q7',
    'Submitted',
    'May 1, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'dh8i9j0k1l2m36485p6q7r8',
    'Submitted',
    'May 1, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '2 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'ei9j0k1l2m3n47596q7r8s9',
    'Submitted',
    'May 1, 2026 15:00:00 MVT',
    742650.0,
    21536.85,
    '3 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'fj0k1l2m3n4o586a7r8s9t0',
    'Submitted',
    'Apr 29, 2026 15:00:00 MVT',
    1125000.0,
    32625.0,
    '1 of 1 for 29 Apr 2026',
    'BML-20260429-TXN-00160',
  ),
  rawTransfer(
    'gk1l2m3n4o5p697b8s9t0u1',
    'Submitted',
    'Apr 27, 2026 15:00:00 MVT',
    1890000.0,
    54810.0,
    '1 of 2 for 27 Apr 2026',
    'BML-20260427-TXN-00155',
  ),
  rawTransfer(
    'hl2m3n4o5p6q708c9t0u1v2',
    'Submitted',
    'Apr 27, 2026 15:00:00 MVT',
    435200.0,
    12620.8,
    '2 of 2 for 27 Apr 2026',
    'BML-20260427-TXN-00155',
  ),
  rawTransfer(
    'im3n4o5p6q7r819d0u1v2w3',
    'Submitted',
    'Apr 25, 2026 15:00:00 MVT',
    650000.0,
    18850.0,
    '1 of 1 for 25 Apr 2026',
    'BML-20260425-TXN-00150',
  ),
  rawTransfer(
    'jn4o5p6q7r8s920e1v2w3x4',
    'Submitted',
    'Apr 23, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 2 for 23 Apr 2026',
    'BML-20260423-TXN-00145',
  ),
  rawTransfer(
    'ko5p6q7r8s9t031f2w3x4y5',
    'Submitted',
    'Apr 23, 2026 15:00:00 MVT',
    315700.0,
    9155.3,
    '2 of 2 for 23 Apr 2026',
    'BML-20260423-TXN-00145',
  ),
  rawTransfer(
    'lp6q7r8s9t0u142g3x4y5z6',
    'Submitted',
    'Apr 21, 2026 15:00:00 MVT',
    1450000.0,
    42050.0,
    '1 of 1 for 21 Apr 2026',
    'BML-20260421-TXN-00140',
  ),
  rawTransfer(
    'mq7r8s9t0u1v253h4y5z6a7',
    'Submitted',
    'Apr 19, 2026 15:00:00 MVT',
    780000.0,
    22620.0,
    '1 of 1 for 19 Apr 2026',
    'BML-20260419-TXN-00135',
  ),
  rawTransfer(
    'nr8s9t0u1v2w364i5z6a7b8',
    'Submitted',
    'Apr 16, 2026 15:00:00 MVT',
    1680000.0,
    48720.0,
    '1 of 2 for 16 Apr 2026',
    'BML-20260416-TXN-00130',
  ),
  rawTransfer(
    'os9t0u1v2w3x475j6a7b8c9',
    'Submitted',
    'Apr 16, 2026 15:00:00 MVT',
    520370.0,
    15090.73,
    '2 of 2 for 16 Apr 2026',
    'BML-20260416-TXN-00130',
  ),
]

// ── Transform ────────────────────────────────────────────────────────────

function parseEffectiveDate(s: string): Date {
  // "May 6, 2026 15:00:00 MVT" — strip trailing tz, parse rest as UTC and
  // back-shift by +8 (MVT is UTC+8). Output is a Date whose toLocaleString
  // in MVT will read as 15:00 on the given day.
  const cleaned = s.replace(/\s+MVT$/, '')
  const ts = Date.parse(cleaned + ' UTC')
  return new Date(ts - 8 * 60 * 60 * 1000)
}

/**
 * Deterministically build the signed adjustment line items for a payout.
 * Fees / VAT / WHT always apply; the remaining kinds vary per payout so the
 * demo shows a range of calculation shapes. The set is open-ended — adding a
 * new kind here makes it flow through the list, the Net adjustments column,
 * and the Payout calculation with zero column changes downstream.
 */
function buildAdjustments(gross: number, idNum: number): PayoutLineItem[] {
  const rng = (i: number) => {
    const x = Math.sin(idNum * 7 + i * 13) * 10000
    return x - Math.floor(x)
  }
  const raw: { kind: PayoutAdjustmentKind; amount: number }[] = []

  // ── Showcase payout (po_10002) ──
  // A single reference payout that carries ONE line item from every category
  // so engineers can see exactly where each adjustment kind belongs in the
  // Payout calculation. Signs follow the documented convention. Fees/VAT/WHT
  // are derived from gross; the rest use realistic fixed sample amounts.
  if (idNum === 10002) {
    const fees = -round2(gross * 0.02826)
    const vat = -round2(Math.abs(fees) * 0.07)
    const wht = round2(gross * 0.000847)
    const showcase: { kind: PayoutAdjustmentKind; amount: number }[] = [
      // Payment activity
      { kind: 'refunds', amount: -round2(200 + rng(2) * 4000) },
      { kind: 'chargebacks', amount: -round2(150 + rng(4) * 600) },
      { kind: 'chargeback_reversals', amount: round2(100 + rng(6) * 500) },
      // Fees and taxes
      { kind: 'fees', amount: fees },
      { kind: 'vat_on_fee', amount: vat },
      { kind: 'wht_adjustment', amount: wht },
      // Reserve adjustments
      { kind: 'reserve_withheld', amount: -round2(400 + rng(12) * 2000) },
      { kind: 'reserve_released', amount: round2(400 + rng(13) * 2000) },
      // Minimum balance adjustments
      {
        kind: 'minimum_balance_retained',
        amount: -round2(500 + rng(10) * 2500),
      },
      {
        kind: 'minimum_balance_released',
        amount: round2(300 + rng(11) * 1500),
      },
      // Account charges
      { kind: 'account_charges', amount: -round2(200 + rng(17) * 800) },
      { kind: 'vat_on_account_charges', amount: -round2(20 + rng(18) * 40) },
      { kind: 'wht_on_account_charges', amount: round2(10 + rng(19) * 20) },
      // Other adjustments
      { kind: 'manual_adjustment', amount: round2(50 + rng(16) * 400) },
      {
        kind: 'negative_balance_recovery',
        amount: -round2(100 + rng(14) * 900),
      },
      { kind: 'other_adjustment', amount: -round2(50 + rng(21) * 300) },
    ]
    return showcase
      .filter((r) => r.amount !== 0)
      .map((r) => ({
        kind: r.kind,
        amount: r.amount,
        label: ADJUSTMENT_META[r.kind].label,
        group: ADJUSTMENT_META[r.kind].group,
      }))
  }

  // ── Payment activity ──
  // Refunds / chargebacks reduce the gross; chargeback reversals add back.
  if (rng(1) > 0.55)
    raw.push({ kind: 'refunds', amount: -round2(200 + rng(2) * 4000) })
  if (rng(3) > 0.85)
    raw.push({ kind: 'chargebacks', amount: -round2(150 + rng(4) * 600) })
  if (rng(5) > 0.9)
    raw.push({
      kind: 'chargeback_reversals',
      amount: round2(100 + rng(6) * 500),
    })

  // ── Fees and taxes ── (transaction fees always apply)
  const fees = -round2(gross * 0.02826)
  const vat = -round2(Math.abs(fees) * 0.07)
  const wht = round2(gross * 0.000847)
  raw.push({ kind: 'fees', amount: fees })
  raw.push({ kind: 'vat_on_fee', amount: vat })
  raw.push({ kind: 'wht_adjustment', amount: wht })

  const mod = idNum % 10

  // ── Reserve adjustments ──
  if (mod === 7)
    raw.push({
      kind: 'reserve_withheld',
      amount: -round2(400 + rng(12) * 2000),
    })
  if (mod === 8)
    raw.push({
      kind: 'reserve_released',
      amount: round2(400 + rng(13) * 2000),
    })

  // ── Minimum balance adjustments ──
  if (mod === 0 || mod === 3 || mod === 6)
    raw.push({
      kind: 'minimum_balance_retained',
      amount: -round2(500 + rng(10) * 2500),
    })
  if (mod === 2)
    raw.push({
      kind: 'minimum_balance_released',
      amount: round2(300 + rng(11) * 1500),
    })

  // ── Account charges ── (merchant-level billing; subtype never exposed)
  if (mod === 1 || mod === 5) {
    const charge = -round2(200 + rng(17) * 800)
    const chargeVat = -round2(Math.abs(charge) * 0.07)
    const chargeWht = round2(Math.abs(charge) * 0.03)
    raw.push({ kind: 'account_charges', amount: charge })
    raw.push({ kind: 'vat_on_account_charges', amount: chargeVat })
    if (rng(18) > 0.5)
      raw.push({ kind: 'wht_on_account_charges', amount: chargeWht })
  }

  // ── Other adjustments ──
  if (mod === 9)
    raw.push({
      kind: 'manual_adjustment',
      amount: (rng(15) > 0.5 ? 1 : -1) * round2(50 + rng(16) * 400),
    })
  if (mod === 4)
    raw.push({
      kind: 'negative_balance_recovery',
      amount: -round2(100 + rng(14) * 900),
    })
  if (mod === 6 && rng(19) > 0.7)
    raw.push({
      kind: 'other_adjustment',
      amount: (rng(20) > 0.5 ? 1 : -1) * round2(50 + rng(21) * 300),
    })

  return raw
    .filter((r) => r.amount !== 0)
    .map((r) => ({
      kind: r.kind,
      amount: r.amount,
      label: ADJUSTMENT_META[r.kind].label,
      group: ADJUSTMENT_META[r.kind].group,
    }))
}

function transformToPayouts(transfers: RawTransferEntry[]): Payout[] {
  const grouped = new Map<string, RawTransferEntry[]>()
  for (const t of transfers) {
    const key = t.externalPayoutReference
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(t)
  }
  const payouts: Payout[] = []
  let payoutCounter = 10001

  // Special-cases — sprinkle in status variety so the detail page can
  // demonstrate all the timeline / pill states.
  const specialStatuses = new Map<string, PayoutStatus>([
    ['BML-20260505-TXN-00179', 'partially_submitted'],
  ])

  Array.from(grouped.entries()).forEach(([bankRef, group]) => {
    const first = group[0]
    const idNum = payoutCounter
    const payoutId = `po_${payoutCounter++}`
    const grossAmount = round2(group.reduce((s, t) => s + t.grossAmount, 0))
    const adjustments = buildAdjustments(grossAmount, idNum)
    const netAdjustments = round2(adjustments.reduce((s, a) => s + a.amount, 0))
    const netAmount = round2(grossAmount + netAdjustments)
    const rawStatus = first.payoutStatus.toLowerCase() as PayoutStatus
    const status: PayoutStatus = specialStatuses.get(bankRef) || rawStatus

    // Distribute the net payout across transfers proportionally to their
    // gross share so the transfer amounts always sum back to Net payout.
    const totalGross = group.reduce((s, t) => s + t.grossAmount, 0) || 1
    let allocated = 0
    const transfers: PayoutTransfer[] = group.map((t, idx) => {
      let transferStatus: TransferStatus = 'submitted'
      if (status === 'partially_submitted' && idx === group.length - 1)
        transferStatus = 'failed'
      if (status === 'pending') transferStatus = 'pending'
      let transferNet: number
      if (idx === group.length - 1) {
        transferNet = round2(netAmount - allocated)
      } else {
        transferNet = round2(netAmount * (t.grossAmount / totalGross))
        allocated = round2(allocated + transferNet)
      }
      return {
        transferId: `pt_${String(idx + 1).padStart(3, '0')}`,
        status: transferStatus,
        netAmount: transferNet,
        bankReference: `${displayBankRef(t.externalPayoutReference)}-${String(idx + 1).padStart(2, '0')}`,
        description: t.description,
      }
    })

    payouts.push({
      payoutId,
      merchantEntity: first.merchantEntity,
      status,
      effectiveDate: first.payoutEffectiveDate,
      effectiveDateRaw: parseEffectiveDate(first.payoutEffectiveDate),
      currency: first.currency,
      grossAmount,
      adjustments,
      netAdjustments,
      netAmount,
      destinationAccount: first.destinationAccount,
      transfers,
    })
  })

  payouts.sort(
    (a, b) => b.effectiveDateRaw.getTime() - a.effectiveDateRaw.getTime(),
  )
  return payouts
}

const round2 = (n: number) => Math.round(n * 100) / 100

// ── Public API ───────────────────────────────────────────────────────────

let cachedPayouts: Payout[] | null = null

export function getAllPayouts(): Payout[] {
  if (!cachedPayouts) cachedPayouts = transformToPayouts(rawTransfers)
  return cachedPayouts
}

export function getPayoutById(id: string): Payout | undefined {
  return getAllPayouts().find((p) => p.payoutId === id)
}

/**
 * Derive a display status from PayoutStatus. A 'submitted' payout always
 * maps to 'paid' regardless of age — bank delivery time isn't observable
 * from our side, so we surface a single honest "Sent" state instead of
 * inventing an "in transit" prediction.
 */
export function getDisplayStatus(payout: Payout): DisplayStatus {
  if (payout.status === 'submitted') return 'paid'
  if (payout.status === 'pending') return 'scheduled'
  return payout.status
}

// ── Bank account details ─────────────────────────────────────────────────

export interface PayoutAccountDetails {
  bank: string
  bankShort: string
  last4: string
  type: string
  created: Date
  accountHolder: string
  origin: { country: string; flag: string }
  fingerprint: string
  routingNumber: string
  bankAccountId: string
}

export function getAccountDetails(payout: Payout): PayoutAccountDetails {
  return {
    bank: BRAND.name,
    bankShort: BRAND.shortCode,
    last4: '4521',
    type: 'Bank account',
    created: new Date('2024-03-15T02:23:00Z'),
    accountHolder: payout.merchantEntity.toUpperCase(),
    origin: { country: 'Malaysia', flag: '🇲🇾' },
    fingerprint: 'RbnICHwZlZtd37Nt',
    routingNumber: BRAND.bank.swift,
    bankAccountId: `ba_1${payout.payoutId
      .replace(/[^a-z0-9]/gi, '')
      .padEnd(20, '0')
      .slice(0, 20)}`,
  }
}

// ── Payout calculation ────────────────────────────────────────────────────
// The Payout detail page renders a dynamic, vertically-stacked calculation:
// the Gross payment amount followed by every applicable signed adjustment,
// optionally grouped. Only-applicable items are stored on the payout, so the
// calculation naturally grows/shrinks with the data — no fixed columns.

export interface PayoutCalcRow {
  label: string
  /** Signed amount. */
  amount: number
  /** The gross row is the calculation's starting point, not an adjustment. */
  isGross?: boolean
}

export interface PayoutCalcSection {
  key: PayoutCalcGroup
  label: string
  rows: PayoutCalcRow[]
}

/**
 * Build the grouped calculation for a payout. The gross payment amount is the
 * first row of the Payment activity group; every applicable adjustment falls
 * into its group. Empty groups are omitted so zero / non-applicable items
 * never render. The signed sum of all rows equals Net payout.
 */
export function getPayoutCalcSections(payout: Payout): PayoutCalcSection[] {
  const byGroup: Record<PayoutCalcGroup, PayoutCalcRow[]> = {
    payment_activity: [],
    fees_taxes: [],
    reserve_adjustments: [],
    minimum_balance_adjustments: [],
    account_charges: [],
    other_adjustments: [],
  }
  byGroup.payment_activity.push({
    label: 'Gross payment amount',
    amount: payout.grossAmount,
    isGross: true,
  })
  for (const a of payout.adjustments) {
    // Skip any zero-amount item so empty rows never render or reserve space.
    if (a.amount === 0) continue
    byGroup[a.group].push({ label: a.label, amount: a.amount })
  }
  const order: PayoutCalcGroup[] = [
    'payment_activity',
    'fees_taxes',
    'reserve_adjustments',
    'minimum_balance_adjustments',
    'account_charges',
    'other_adjustments',
  ]
  return order
    .map((key) => ({ key, label: GROUP_LABEL[key], rows: byGroup[key] }))
    .filter((s) => s.rows.length > 0)
}

// ── Timeline ─────────────────────────────────────────────────────────────

export interface TimelineStep {
  /** Headline label, e.g. "Funds arrived" */
  label: string
  /** When this step happened (or is expected to). null for non-temporal. */
  date: Date | null
  /** True if this step has already occurred. */
  done: boolean
  /** Optional extra detail line shown beneath the label. */
  detail?: string
  /** Optional range label (e.g. for the in-transit window). */
  rangeLabel?: string
}

const ONE_DAY = 24 * 60 * 60 * 1000

// MVT is UTC+8. To snap a Date to "00:01 MVT" on the calendar day that
// `eff` falls into (in MVT) minus 2 days: shift into MVT, floor to the day
// boundary, subtract 2 days, add 1 minute, shift back to UTC.
const MVT_OFFSET_MS = 8 * 60 * 60 * 1000
function initiatedAtMVTMidnight(eff: Date): Date {
  const mytTs = eff.getTime() + MVT_OFFSET_MS
  const dayMs = 24 * 60 * 60 * 1000
  const mytDayStart = Math.floor(mytTs / dayMs) * dayMs
  const initiatedMytTs = mytDayStart - 2 * dayMs + 60 * 1000 // 00:01 MVT
  return new Date(initiatedMytTs - MVT_OFFSET_MS)
}

export function getTimeline(payout: Payout): TimelineStep[] {
  const eff = payout.effectiveDateRaw
  const initiated = initiatedAtMVTMidnight(eff)

  const display = getDisplayStatus(payout)

  // Bank-side delivery (in-transit window, "funds arriving") is intentionally
  // omitted — we can't reliably predict when CIMB credits the destination
  // account, so we only show the events we can speak to: the payout's
  // initiation, and any terminal status the payout itself reaches.
  switch (display) {
    case 'paid':
      return [
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
    case 'scheduled':
      return [
        {
          label: 'Payout scheduled',
          date: eff,
          done: false,
          detail:
            'This payout will be created automatically on the scheduled date and submitted to your bank.',
        },
      ]
    case 'partially_submitted':
      return [
        {
          label: 'Partially submitted',
          date: eff,
          done: true,
          detail:
            'Some transfers in this payout failed. Review the transfers list and contact Support if you need a re-attempt.',
        },
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
    case 'failed':
      return [
        {
          label: 'Payout failed',
          date: eff,
          done: true,
          detail: `Submission to ${BRAND.name} failed. Funds remain in your Available balance and the payout will retry on the next cycle.`,
        },
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
    case 'cancelled':
      return [
        { label: 'Payout cancelled', date: eff, done: true },
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
  }
}

function shortDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * The moment the payout left our system (auto-initiated). For sent
 * payouts this is the date/time we'd surface in the page header, since
 * bank-side delivery isn't observable.
 */
export function getInitiatedAt(payout: Payout): Date {
  return initiatedAtMVTMidnight(payout.effectiveDateRaw)
}

// ── Trace ID ─────────────────────────────────────────────────────────────

/**
 * Bank-side trace ID merchants reconcile against their statements. Format
 * mimics Malaysian interbank reference style.
 */
export function getPayoutTraceId(payout: Payout): string {
  const eff = payout.effectiveDateRaw
  const yyyymmdd =
    eff.getFullYear().toString() +
    String(eff.getMonth() + 1).padStart(2, '0') +
    String(eff.getDate()).padStart(2, '0')
  const tail = payout.payoutId.slice(-6).toUpperCase().padStart(6, '0')
  return `MY2ON${yyyymmdd}${tail}`
}
